/**
 * Office.js compatibility — History API and String prototype protection.
 *
 * Office.js nullifies `window.history.pushState` and `replaceState` which
 * breaks Next.js App Router navigation. This script uses
 * `Object.defineProperty` to lock in the correct methods from
 * `History.prototype` *before* Office.js loads, so when Office.js later
 * tries to assign `null` the no-op setter silently ignores it.
 *
 * Injected as an inline <script> by `<HostProvider>` whenever the
 * current `AppSurface` is an Office add-in.
 */

/**
 * Raw JavaScript source suitable for injection via `dangerouslySetInnerHTML`.
 * Must run synchronously before Office.js loads (and before React hydrates).
 */
export const HISTORY_RESTORE_SCRIPT = `
(function() {
  var h = window.history;
  var ps = History.prototype.pushState.bind(h);
  var rs = History.prototype.replaceState.bind(h);
  Object.defineProperty(h, 'pushState', {
    get: function() { return ps; },
    set: function() {},
    configurable: false
  });
  Object.defineProperty(h, 'replaceState', {
    get: function() { return rs; },
    set: function() {},
    configurable: false
  });
})();`;

/**
 * Office hosts load `MicrosoftAjax.js`, which replaces
 * `String.prototype.startsWith` / `endsWith` with versions that ignore the
 * position argument. Turbopack's chunk loader checks `.js` / `.css` with
 * `url.startsWith(ext, end - ext.length)`, so with the override every
 * lazily-loaded chunk fails with "can't infer type of chunk from URL" and
 * the message that needed it renders as "This message couldn't be displayed".
 *
 * Installs spec-compliant implementations (keeping the native ones when they
 * already behave) and locks them the same way as the History API above, so
 * the later assignment from MicrosoftAjax is silently ignored.
 */
export const STRING_PROTOTYPE_RESTORE_SCRIPT = `
(function() {
  var sp = String.prototype;
  function lock(name, impl) {
    Object.defineProperty(sp, name, {
      get: function() { return impl; },
      set: function() {},
      configurable: false
    });
  }
  function startsWith(search, position) {
    var str = String(this);
    var needle = String(search);
    var start = position > 0 ? Math.floor(position) : 0;
    return str.substr(start, needle.length) === needle;
  }
  function endsWith(search, endPosition) {
    var str = String(this);
    var needle = String(search);
    var end = endPosition === undefined
      ? str.length
      : Math.min(Math.max(Math.floor(endPosition) || 0, 0), str.length);
    return end - needle.length >= 0 && str.substr(end - needle.length, needle.length) === needle;
  }
  lock('startsWith', 'ab'.startsWith('b', 1) ? sp.startsWith : startsWith);
  lock('endsWith', 'ab'.endsWith('a', 1) ? sp.endsWith : endsWith);
})();`;
